import { Info } from "lucide-react";
import { useGetIdentity, useShowContext, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Show } from "@/components/admin/show";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton } from "../design/ListStates";
import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../misc/locale";
import { AdvisorInvoiceNotice } from "../operations/PayableRequirementHint";
import { formatMxn } from "../quoter/PlanSchedule";
import type { AppRole } from "../types";
import type { PayableSummary } from "./invoicingTypes";
import { PayableActions } from "./PayableActions";

const EMPTY = "—";

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(APP_LOCALE, NUMERIC_DATE_FORMAT)
    : EMPTY;

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm tabular-nums">{children}</dd>
  </div>
);

/** The linked receivable status: information only, it never blocks the payment. */
const ReceivableInfoBadge = ({ payable }: { payable: PayableSummary }) => {
  const translate = useTranslate();
  if (!payable.receivable_status) return null;
  return (
    <p
      data-testid="receivable-info"
      className="inline-flex min-h-7 items-center gap-1.5 rounded-full border bg-muted/40 px-3 text-sm text-muted-foreground"
    >
      <Info className="size-4" aria-hidden="true" />
      {translate("crm.invoicing.payables.receivable_info", {
        status: translate(
          `crm.invoicing.status.receivable.${payable.receivable_status}`,
        ),
      })}
    </p>
  );
};

const PayableShowLayout = () => {
  const { record, isPending } = useShowContext<PayableSummary>();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  if (isPending || !record || !identity) return <ListCardsSkeleton rows={3} />;

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-semibold tabular-nums">
            {record.operation_id}
          </h2>
          <span
            data-testid="payable-status"
            className="inline-flex min-h-7 items-center rounded-full border px-3 text-sm font-medium"
          >
            {translate(`crm.invoicing.status.payable.${record.status}`)}
          </span>
          <ReceivableInfoBadge payable={record} />
        </div>
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Fact label={translate("crm.invoicing.fields.payee")}>
            {record.payee_name ?? EMPTY} (
            {translate(`crm.invoicing.payee_role.${record.payee_role}`)})
          </Fact>
          <Fact label={translate("crm.invoicing.fields.project_or_address")}>
            {record.project_code ?? record.property_address ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.unit")}>
            {record.unit_identifier ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.basis")}>
            {translate(`crm.invoicing.basis.${record.basis}`)}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.level")}>
            {record.level_code
              ? translate(`crm.invoicing.level.${record.level_code}`)
              : EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.rate")}>
            {record.rate_pct == null ? EMPTY : `${record.rate_pct}%`}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.accumulated_sales")}>
            {record.accumulated_sales ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.subtotal")}>
            {formatMxn(record.subtotal)}
          </Fact>
          <Fact
            label={translate("crm.invoicing.fields.iva", {
              pct: record.iva_pct,
            })}
          >
            {formatMxn(record.iva_amount)}
          </Fact>
          <Fact label={translate("crm.invoicing.fields.total")}>
            <span className="font-semibold">
              {formatMxn(record.total_amount)}
            </span>
          </Fact>
          <Fact label={translate("crm.invoicing.payables.paid_on")}>
            {formatDate(record.paid_at)}
          </Fact>
          <Fact label={translate("crm.invoicing.payables.reference")}>
            <span data-testid="payment-reference">
              {record.payment_reference ?? EMPTY}
            </span>
          </Fact>
        </dl>
        {record.last_rejection_reason ? (
          <p className="text-sm text-[#B3321C]" data-testid="last-rejection">
            {translate("crm.invoicing.vobo.last_rejection", {
              reason: record.last_rejection_reason,
            })}
          </p>
        ) : null}
        {record.cancel_reason ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.invoicing.fields.cancel_reason")}:{" "}
            {record.cancel_reason}
          </p>
        ) : null}
        <AdvisorInvoiceNotice role={identity.role as AppRole} />
        <PayableActions payable={record} role={identity.role as AppRole} />
      </CardContent>
    </Card>
  );
};

/**
 * Almikia Epic E6 (TASK-041): detail of a payable. Shows its own snapshots
 * (level, rate, accumulated sales, amounts) and the linked receivable status
 * as an information badge; it never reads operations or clients and offers no
 * link into them.
 */
export const PayableShow = () => (
  <Show actions={false}>
    <PayableShowLayout />
  </Show>
);
